import type { ReactNode } from 'react';
import { Box, ThemeProvider } from '@mui/material';

import { useEffectiveWebsiteTheme } from '@/hooks/use-effective-website-theme';
import { resolveButtonFontKey, useGoogleFonts } from '@/styles/theme/create-unit-site-theme';
import type { SiteThemeConfig } from '@/types/database';
import { useAccessibilityPreviewPreferences } from './accessibility-preview-context';

// Every Site Styles screen previews the live draft in the unit's own
// theme, not the Studio app's own dark operator theme — same reasoning as
// StudioCanvas's own nested ThemeProvider (see its comment): this is the
// one place editor UI must render in the theme being edited. Also layers
// in the drawer's own accessibility-preview selection (see
// accessibility-preview-context.tsx and SiteStylesDrawer.tsx) via the same
// derivation the published site itself uses, so this preview is honest
// about what a visitor would actually see with that preference enabled.
export function PreviewCard({ themeConfig, children, dense }: { themeConfig: SiteThemeConfig; children: ReactNode; dense?: boolean }) {
  const previewPreferences = useAccessibilityPreviewPreferences();
  const theme = useEffectiveWebsiteTheme(themeConfig, previewPreferences);
  useGoogleFonts([themeConfig.fonts.headingFont, themeConfig.fonts.paragraphFont, resolveButtonFontKey(themeConfig.fonts), themeConfig.fonts.miscFont]);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <ThemeProvider theme={theme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', p: dense ? 2 : 3 }}>{children}</Box>
      </ThemeProvider>
    </Box>
  );
}
